import classNames from 'classnames';
import { Gamepad2, ShoppingBag, Star } from 'lucide-react';
import type { FC } from 'react';
import { OfferCategory } from 'shared/constants/offer-categories';
import Coin from 'src/assets/images/icons/custom-icons/Coin';
import Xp from 'src/assets/images/icons/custom-icons/Xp';
import TimeLeft from 'src/components/TimeLeft/TimeLeft';
import { useUserCurrency } from 'src/hooks/use-user-currency';
import { getTopGameBorderClassName, getTopGameMedal } from 'src/utils/top-game-medal';

export interface GameCardDataProps {
  cardType?: 'game' | 'deal';
  title: string;
  points: number;
  xp: number;
  rating: number;
  imageUrl?: string;
  pointsEarned?: number;
  fullPointsSum?: number;
  xpEarned?: number;
  fullXpSum?: number;
  expiresOn?: string | null;
  timeLimitHours?: number | null;
  gameId?: number;
  dealId?: number;
  signature?: string;
  gameTileDetailsDto?: string;
  sessionUid?: string | null;
  osTypeId?: number;
  partnerDealId?: string;
  entryLink?: string;
  actionLabel?: string;
  categories?: OfferCategory[];
  isNew?: boolean;
  partnerName?: string;
}

interface GameCardProps {
  gameData: GameCardDataProps;
  onClick?: () => void;
  isTopGame?: boolean;
  topGameRank?: number;
  isGameInProgress?: boolean;
  isGamesPage?: boolean;
  isDisabled?: boolean;
  className?: string;
  imageLoading?: 'eager' | 'lazy';
  imageFetchPriority?: 'high' | 'low' | 'auto';
}

const format = (value: number) => value.toLocaleString('en-US');

const GameCard: FC<GameCardProps> = ({
  gameData,
  onClick,
  isTopGame = false,
  topGameRank = 1,
  isGameInProgress = false,
  isGamesPage = false,
  isDisabled = false,
  className,
  imageLoading = 'lazy',
  imageFetchPriority = 'auto',
}) => {
  const { formatPoints } = useUserCurrency();
  const isDeal = gameData.cardType === 'deal';
  const FallbackIcon = isDeal ? ShoppingBag : Gamepad2;
  const actionLabel = gameData.actionLabel ?? (isGameInProgress ? 'Keep playing' : isDeal ? 'View deal' : 'View game');
  const pointsEarned = gameData.pointsEarned ?? 0;
  const fullPointsSum = gameData.fullPointsSum ?? gameData.points;
  const xpEarned = gameData.xpEarned ?? 0;
  const fullXpSum = gameData.fullXpSum ?? gameData.xp;
  const topGameMedal = getTopGameMedal(topGameRank);
  const topGameBorderClassName = getTopGameBorderClassName(topGameRank);
  const pointsProgress =
    gameData.fullPointsSum && gameData.fullPointsSum > 0
      ? Math.min(100, ((gameData.pointsEarned ?? 0) / gameData.fullPointsSum) * 100)
      : 0;
  const formatProgress = (earned: number, total: number) => `${formatPoints(earned)}/${formatPoints(total)}`;

  const renderCompactStat = (
    icon: typeof Coin | typeof Xp,
    label: string,
    value: string,
    className: string,
    iconClassName: string
  ) => {
    const Icon = icon;

    return (
      <div className={classNames('min-w-0 rounded-lg px-2 py-1.5', className)}>
        <div className="flex min-w-0 items-center gap-1">
          <Icon className={classNames('h-3.5 w-3.5 shrink-0', iconClassName)} />
          <span className="truncate font-manrope text-[11px] font-semibold leading-3 text-slate-500">{label}</span>
        </div>
        <div className="mt-1 min-w-0 break-all font-manrope text-[13px] font-bold leading-4 text-slate-900 tabular-nums">
          {value}
        </div>
      </div>
    );
  };

  if (isGameInProgress) {
    return (
      <article
        className={classNames(
          'flex h-full min-w-0 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm',
          isDisabled
            ? 'cursor-pointer opacity-60 grayscale saturate-0'
            : 'group transition hover:-translate-y-0.5 hover:border-[#5b98d1]/50 hover:shadow-lg',
          isGamesPage ? 'w-full' : 'w-[220px] shrink-0 md:w-[230px]',
          className
        )}
      >
        <button
          type="button"
          onClick={onClick}
          disabled={isDisabled}
          aria-disabled={isDisabled}
          aria-label={`${actionLabel}: ${gameData.title}`}
          className="flex h-full w-full min-w-0 flex-col p-3 text-left cursor-pointer"
        >
          <div className="flex min-w-0 items-center gap-3">
            <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-gradient-to-br from-blue-50 to-indigo-100">
              {gameData.imageUrl ? (
                <img
                  src={gameData.imageUrl}
                  alt=""
                  loading={imageLoading}
                  fetchPriority={imageFetchPriority}
                  decoding="async"
                  width={56}
                  height={56}
                  className={classNames(
                    'h-full w-full object-cover',
                    !isDisabled && 'transition duration-300 group-hover:scale-105'
                  )}
                />
              ) : (
                <FallbackIcon className="absolute left-1/2 top-1/2 h-7 w-7 -translate-x-1/2 -translate-y-1/2 text-[#3f54a4]" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <h3 className="truncate font-manrope text-sm font-bold leading-5 text-slate-900">{gameData.title}</h3>
              {gameData.partnerName && (
                <p className="mt-0.5 truncate font-manrope text-[10px] font-medium leading-3 text-slate-400">
                  {gameData.partnerName}
                </p>
              )}
              {gameData.expiresOn || gameData.timeLimitHours ? (
                <TimeLeft
                  expiresOn={gameData.expiresOn}
                  timeLimitHours={gameData.timeLimitHours}
                  layout="compact-with-left-suffix"
                  leftSuffix="left"
                  className="mt-1 max-w-full"
                  iconClassName="h-3.5 w-3.5"
                  valueClassName="text-[11px] font-semibold text-slate-500"
                  unitClassName="text-[11px] font-semibold text-slate-500"
                />
              ) : (
                <span className="mt-1 block truncate font-manrope text-[11px] font-semibold leading-3 text-slate-500">
                  No time limit
                </span>
              )}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
            {renderCompactStat(
              Coin,
              'Earnings',
              formatProgress(pointsEarned, fullPointsSum),
              'bg-pink-50',
              'text-[#9b466f]'
            )}
            {renderCompactStat(Xp, 'XP earned', formatProgress(xpEarned, fullXpSum), 'bg-blue-50', 'text-[#3f54a4]')}
          </div>

          <span className="sr-only">{actionLabel}</span>
        </button>
      </article>
    );
  }

  return (
    <article
      className={classNames(
        'flex h-full min-w-0 flex-col overflow-hidden rounded-2xl bg-white shadow-sm',
        isTopGame && topGameBorderClassName ? topGameBorderClassName : 'border border-slate-200',
        isDisabled
          ? 'cursor-pointer opacity-60 grayscale saturate-0'
          : classNames(
              'group transition hover:-translate-y-1 hover:shadow-xl',
              !(isTopGame && topGameBorderClassName) && 'hover:border-[#5b98d1]/50'
            ),
        isGamesPage ? 'w-full' : 'w-[220px] shrink-0 md:w-[250px]',
        className
      )}
    >
      <button
        type="button"
        onClick={onClick}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        aria-label={`${actionLabel}: ${gameData.title}`}
        className="flex h-full min-w-0 flex-col text-left cursor-pointer"
      >
        <div className="relative aspect-[2/1] w-full overflow-hidden bg-gradient-to-br from-blue-50 to-indigo-100">
          {gameData.imageUrl ? (
            <img
              src={gameData.imageUrl}
              alt=""
              loading={imageLoading}
              fetchPriority={imageFetchPriority}
              decoding="async"
              width={500}
              height={250}
              className={classNames(
                'h-full w-full object-cover',
                !isDisabled && 'transition duration-300 group-hover:scale-105'
              )}
            />
          ) : (
            <FallbackIcon className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 text-[#3f54a4]" />
          )}
          {isTopGame && topGameMedal && (
            <span
              className="absolute left-3 top-3 inline-flex items-center justify-center rounded-full bg-white/95 px-2 py-1 text-lg leading-none shadow"
              aria-label={`Top game rank ${topGameRank}`}
            >
              {topGameMedal}
            </span>
          )}
          {isGameInProgress && (
            <span className="absolute right-3 top-3 rounded-full bg-[#c26a93] px-2.5 py-1 text-xs font-semibold text-white shadow">
              In progress
            </span>
          )}
        </div>

        <div className="flex w-full flex-1 flex-col gap-2.5 p-3">
          <div className="flex min-w-0 items-start justify-between gap-2">
            <h3 className="line-clamp-2 min-w-0 flex-1 font-manrope text-sm font-bold leading-5 text-slate-900">
              {gameData.title}
            </h3>
            {gameData.rating > 0 && (
              <span className="inline-flex shrink-0 items-center gap-1 pt-0.5 text-xs text-slate-500">
                <Star className="h-3.5 w-3.5 fill-[#c26a93] text-[#c26a93]" />
                {gameData.rating.toFixed(1)}
              </span>
            )}
          </div>
          {gameData.partnerName && (
            <p className="-mt-1 truncate font-manrope text-[10px] font-medium leading-3 text-slate-400">
              {gameData.partnerName}
            </p>
          )}

          <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2">
            {gameData.points > 0 && (
              <span className="inline-flex min-h-8 min-w-0 flex-wrap items-center justify-center gap-1 rounded-lg bg-pink-50 px-2 py-1 text-center text-xs font-semibold text-[#9b466f]">
                <Coin className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 break-all">{formatPoints(gameData.points)}</span>
              </span>
            )}
            {gameData.xp > 0 && (
              <span className="inline-flex min-h-8 min-w-0 flex-wrap items-center justify-center gap-1 rounded-lg bg-blue-50 px-2 py-1 text-center text-xs font-semibold text-[#3f54a4]">
                <Xp className="h-3.5 w-3.5 shrink-0" />
                <span className="min-w-0 break-all">{format(gameData.xp)} XP</span>
              </span>
            )}
          </div>

          {isGameInProgress && gameData.fullPointsSum ? (
            <div className="mt-auto">
              <div className="mb-1 flex justify-between text-xs text-slate-500">
                <span>Earned {formatPoints(gameData.pointsEarned ?? 0)}</span>
                <span>{Math.round(pointsProgress)}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#c26a93] to-[#5b98d1]"
                  style={{ width: `${pointsProgress}%` }}
                />
              </div>
            </div>
          ) : gameData.expiresOn || gameData.timeLimitHours ? (
            <div className="mt-auto">
              <TimeLeft
                expiresOn={gameData.expiresOn}
                timeLimitHours={gameData.timeLimitHours}
                layout="compact-combined"
                iconClassName="h-3.5 w-3.5"
                valueClassName="text-xs text-slate-500"
              />
            </div>
          ) : null}

          <span className="sr-only">{actionLabel}</span>
        </div>
      </button>
    </article>
  );
};

export default GameCard;
